import React, { FC } from "react";
import { AutoCompleteSuggestionsInput, Suggestion } from "@xapp/components";

const fontPickerContainerStyle: React.CSSProperties = {
    position: "relative",
    zIndex: 1001,
    width: "100%",
    overflow: "visible",
};

// Custom CSS for font picker layout
const fontPickerStyles = `
.font-picker-container {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    min-height: 31px;
    border-bottom: solid 1px #E5E5E5;
    border-top: solid 1px #E5E5E5;
    align-items: stretch;
}
.font-picker-container + .font-picker-container {
    border-top-width: 0;
}
.font-picker-label {
    display: inline-flex;
    flex: 0 0 auto;
    margin-right: 1em;
    align-items: center;
    white-space: nowrap;
    min-width: 50px;
}
.font-picker-content {
    flex: 1 1 auto;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    padding: 1px 0;
    overflow: visible;
    position: relative;
}
`;

export interface WidgetFontFamilyPickerProps {
    readonly label?: string;
    readonly value: string | undefined;
    readonly defaultValue: string | undefined;
    readonly disabled?: boolean;
    onChange(value: string, link?: string): void;
}

interface GoogleFont {
    name: string;
    link: string;
}

const GOOGLE_FONTS: GoogleFont[] = [
    { name: "Roboto", link: "https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap" },
    {
        name: "Open Sans",
        link: "https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap",
    },
    { name: "Lato", link: "https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700&display=swap" },
    { name: "Noto Sans", link: "https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500;600;700&display=swap" },
    {
        name: "Montserrat",
        link: "https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap",
    },
    { name: "Oswald", link: "https://fonts.googleapis.com/css2?family=Oswald:wght@300;400;500;600;700&display=swap" },
    { name: "Poppins", link: "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" },
    { name: "Raleway", link: "https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;500;600;700&display=swap" },
    { name: "PT Sans", link: "https://fonts.googleapis.com/css2?family=PT+Sans:wght@400;700&display=swap" },
    {
        name: "Merriweather",
        link: "https://fonts.googleapis.com/css2?family=Merriweather:wght@300;400;700&display=swap",
    },
];

export const WidgetFontFamilyPicker: FC<WidgetFontFamilyPickerProps> = (props) => {
    const handleDirectChange = (selection: Suggestion | string) => {
        let fontName: string;
        let fontLink: string | undefined;

        if (typeof selection === "string") {
            fontName = selection;
        } else {
            const googleFont = GOOGLE_FONTS.find((gf) => gf.name === selection.id);
            if (googleFont) {
                fontName = googleFont.name;
                fontLink = googleFont.link;
            } else {
                fontName = selection.label;
            }
        }

        props.onChange(fontName, fontLink);
    };

    const systemFonts: string[] = [
        "Arial",
        "Baskerville",
        "Brush Script MT",
        "Comic Sans MS",
        "Courier New",
        "Garamond",
        "Georgia",
        "Gill Sans",
        "Helvetica",
        "Impact",
        "Lucida",
        "Monaco",
        "Monospace",
        "Palatino",
        "RobotoRegular",
        "Sans-Serif",
        "Serif",
        "Tahoma",
        "Times New Roman",
        "Trebuchet MS",
        "Verdana",
    ];

    const googleFontSuggestions: Suggestion[] = GOOGLE_FONTS.map((font) => ({
        label: `${font.name} (Google Font)`,
        id: font.name,
    }));

    const systemFontSuggestions: Suggestion[] = systemFonts.map((font) => ({
        label: font,
        id: font,
    }));

    const allSuggestions = [...googleFontSuggestions, ...systemFontSuggestions];

    return (
        <>
            <style>{fontPickerStyles}</style>
            <div className="font-picker-container">
                {props.label && <label className="font-picker-label">{props.label}</label>}
                <div className="font-picker-content">
                    <div style={fontPickerContainerStyle}>
                        <AutoCompleteSuggestionsInput
                            suggestions={allSuggestions}
                            echo={true}
                            variation="standard"
                            disabled={props.disabled}
                            value={props.value || ""}
                            label=""
                            placeholder="Enter font name or select from suggestions"
                            {...{ wrapperStyle: { paddingBottom: "0px" } }}
                            onChange={handleDirectChange}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};
